// Zoom and pan for the bracket canvas. Drag to pan, ctrl/cmd + wheel or pinch
// to zoom, plus the +, −, Fit, and fullscreen buttons.
export function makeViewport(el, canvas, { pctEl = null, minScale = 0.25, maxScale = 2, interactive = true } = {}) {
  let x = 0, y = 0, k = 1, cw = 1, ch = 1
  const apply = () => {
    canvas.style.transform = `translate(${x}px, ${y}px) scale(${k})`
    if (pctEl) pctEl.textContent = Math.round(k * 100) + '%'
  }
  const clampK = v => Math.min(maxScale, Math.max(minScale, v))
  const zoomAt = (px, py, f) => {
    const nk = clampK(k * f), r = nk / k
    x = px - (px - x) * r
    y = py - (py - y) * r
    k = nk
    apply()
  }
  const center = () => ({ x: el.clientWidth / 2, y: el.clientHeight / 2 })

  if (interactive) {
    el.addEventListener('wheel', e => {
      if (!(e.ctrlKey || e.metaKey)) return
      e.preventDefault()
      const r = el.getBoundingClientRect()
      zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.01))
    }, { passive: false })

    const pts = new Map()
    let last = null, pinch = null, moved = false
    const pinchState = () => {
      const [a, b] = [...pts.values()]
      const r = el.getBoundingClientRect()
      return { d: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2 - r.left, cy: (a.y + b.y) / 2 - r.top }
    }
    el.addEventListener('pointerdown', e => {
      if (e.target.closest('[data-js-no-pan], [draggable="true"]') || e.button > 0) return
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (pts.size === 1) { last = { x: e.clientX, y: e.clientY }; moved = false; el.classList.add('is-panning') }
      else if (pts.size === 2) pinch = pinchState()
    })
    window.addEventListener('pointermove', e => {
      if (!pts.has(e.pointerId)) return
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (pts.size === 2 && pinch) {
        const p = pinchState()
        zoomAt(p.cx, p.cy, p.d / pinch.d)
        x += p.cx - pinch.cx; y += p.cy - pinch.cy
        apply(); pinch = p; moved = true
      } else if (pts.size === 1 && last) {
        const dx = e.clientX - last.x, dy = e.clientY - last.y
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true
        x += dx; y += dy
        last = { x: e.clientX, y: e.clientY }
        apply()
      }
    })
    const up = e => {
      pts.delete(e.pointerId)
      if (pts.size < 2) pinch = null
      if (pts.size === 0) { last = null; el.classList.remove('is-panning') }
    }
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    // A drag should not count as a click on whatever is under the pointer.
    el.addEventListener('click', e => { if (moved) { e.stopPropagation(); e.preventDefault(); moved = false } }, true)
  }

  const api = {
    get scale() { return k },
    setSize(w, h) { cw = w; ch = h },
    fit(topOffset = 44, floor = 0.5) {
      const vw = el.clientWidth, vh = el.clientHeight
      k = Math.min(1, Math.max(floor, Math.min(vw / cw, (vh - topOffset) / ch)))
      x = Math.max(0, (vw - cw * k) / 2)
      y = Math.max(topOffset, (vh - ch * k) / 2)
      apply()
    },
    // Frames one region of the canvas, e.g. the rounds being played, at up to
    // 100%. `rect` is in canvas coordinates.
    fitRect(rect, topOffset = 44, floor = 0.5, pad = 24) {
      const vw = el.clientWidth, vh = el.clientHeight
      k = Math.min(1, Math.max(floor, Math.min((vw - pad * 2) / rect.w, (vh - topOffset - pad) / rect.h)))
      // Centre the region when it fits; otherwise pin its left edge, the way
      // fit() does, rather than centring and cutting off both ends.
      x = rect.w * k <= vw - pad * 2 ? (vw - rect.w * k) / 2 - rect.x * k : pad - rect.x * k
      y = topOffset + Math.max(0, (vh - topOffset - rect.h * k) / 2) - rect.y * k
      apply()
    },
    // The scale fit() would use, to decide whether the whole canvas is readable.
    fitScale(topOffset = 44) {
      return Math.min(1, el.clientWidth / cw, (el.clientHeight - topOffset) / ch)
    },
    // Whether a canvas region fits across the viewport at the minimum zoom.
    fitsAcross(rect, floor = 0.5, pad = 24) {
      return rect.w * floor <= el.clientWidth - pad * 2
    },
    fitAll() {
      const vw = el.clientWidth, vh = el.clientHeight
      k = clampK(Math.min(vw / cw, vh / ch))
      x = (vw - cw * k) / 2
      y = (vh - ch * k) / 2
      apply()
    },
    zoomIn() { const c = center(); zoomAt(c.x, c.y, 1.2) },
    zoomOut() { const c = center(); zoomAt(c.x, c.y, 1 / 1.2) },
    panTo(cx, cy) { x = 16 - cx * k; y = 16 - cy * k; apply() },
    fullscreen() {
      if (document.fullscreenElement) document.exitFullscreen()
      else if (el.requestFullscreen) el.requestFullscreen()
    }
  }
  return api
}
